// src/components/CategoryEditor.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  margin-top: 24px;
  padding: 12px;
  border: 1px solid #444;
  color: #e1d6c0;          /* Admin font color */
  font-family: sans-serif;
`;

const Title = styled.h3`
  margin-bottom: 16px;
  color: #e1d6c0;
`;

const ItemRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #666;
    border-radius: 4px;
    background: #111;
    color: #fff;             /* input text color */
  }

  button {
    padding: 6px 12px;
    border: 1px solid #e1d6c0;
    border-radius: 4px;
    background: transparent;
    color: #e1d6c0;          /* button font color */
    cursor: pointer;
    &:hover { opacity: 0.8; }
  }
`;

const AddButton = styled.button`
  margin-top: 12px;
  padding: 6px 12px;
  border: 1px solid #e1d6c0;
  border-radius: 4px;
  background: transparent;
  color: #e1d6c0;            /* add button color */
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

export default function CategoryEditor({ cat, token, onChange }) {
  const isNew = !cat;
  const [name, setName] = useState(cat?.name || '');
  const [position, setPosition] = useState(cat?.position || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!isNew);

  // Load items for this category
  useEffect(() => {
    if (!cat) return;
    fetch(`/api/admin/categories/${cat.id}/items`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => r.json())
      .then(data => setItems(data))
      .catch(() => alert('Failed to load items'))
      .finally(() => setLoading(false));
  }, [cat, token]);

  const handleSave = (index) => {
    const it = items[index];
    const method = it.id ? 'PUT' : 'POST';
    const url = it.id
      ? `/api/admin/items/${it.id}`
      : `/api/admin/categories/${cat.id}/items`;

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({
        name: it.name,
        price: it.price,
        position: index + 1,
      })
    })
      .then(r => r.json())
      .then(saved => {
        const updated = [...items];
        updated[index] = saved;
        setItems(updated);
        onChange();
      })
      .catch(() => alert('Failed to save item'));
  };

  const handleDelete = (index) => {
    const it = items[index];
    if (it.id) {
      fetch(`/api/admin/items/${it.id}`, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(r => {
          if (!r.ok) throw new Error();
          const updated = items.filter((_, i) => i !== index);
          setItems(updated);
          onChange();
        })
        .catch(() => alert('Failed to delete item'));
    } else {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleAdd = () => {
    setItems([...items, { name: '', price: '' }]);
  };

  const handleCreate = () => {
    if (!name || !position) return;
    fetch('/api/admin/categories', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({ name, position: +position })
    })
      .then(r => r.json())
      .then(() => {
        setName('');
        setPosition('');
        onChange();
      })
      .catch(() => alert('Failed to create category'));
  };

  const handleUpdate = () => {
    fetch(`/api/admin/categories/${cat.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({ name, position: +position })
    })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => onChange())
      .catch(() => alert('Failed to update category'));
  };

  if (loading && !isNew) return <p>Loading items…</p>;

  return (
    <Wrapper>
      <Title>{isNew ? 'New Category' : cat.name}</Title>
      {isNew ? (
        // NEW CATEGORY form
        <ItemRow>
          <input
            placeholder="Category Name"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <input
            type="number"
            placeholder="Position"
            value={position}
            onChange={e => setPosition(e.target.value)}
            style={{ maxWidth: 80 }}
          />
          <button onClick={handleCreate} disabled={!name || !position}>
            Create
          </button>
        </ItemRow>
      ) : (
        // EXISTING category’s items
        <>
          {/* NEW: editable category header row */}
          <ItemRow>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ flex: 2 }}
              />
              <input
                type="number"
                value={position}
                onChange={e => setPosition(e.target.value)}
                style={{ maxWidth: 80 }}
              />
              <button onClick={handleUpdate}>
                Save Category
              </button>
            </ItemRow>

            {/* existing category’s items */}
            {items.map((it, i) => (
              <ItemRow key={it.id || i}>
                <input
                  placeholder="Name"
                  value={it.name}
                  onChange={e => {
                    const copy = [...items];
                    copy[i].name = e.target.value;
                    setItems(copy);
                  }}
                />
                <input
                  placeholder="Price"
                  value={it.price}
                  onChange={e => {
                    const copy = [...items];
                    copy[i].price = e.target.value;
                    setItems(copy);
                  }}
                  style={{ maxWidth: 80 }}
                />
                <button onClick={() => handleSave(i)}>Save</button>
                <button onClick={() => handleDelete(i)}>Delete</button>
              </ItemRow>
            ))}

            <AddButton onClick={handleAdd}>Add Item</AddButton>
          </>
        )
      }
    </Wrapper>
  );
}